import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Badge,
  Tooltip,
  Fade,
  Zoom,
} from '@mui/material';
import { 
  Add, 
  Delete, 
  Save, 
  Edit,
  VideoFile,
  Image,
  Work,
  CheckCircle,
  CloudUpload,
  PlayArrow,
  Pause,
  Refresh,
} from '@mui/icons-material';
import { MediaPreview } from './MediaPreview';
import { MediaManager } from './MediaManager';

interface MediaItem {
  id: string;
  title: string;
  description: string;
  src: string;
  type: 'video' | 'image';
}

interface MediaManagementProps {
  onTempUpload?: (media: any) => void;
  onCancelUpload?: (mediaId: string) => Promise<void>;
}

export const MediaManagement: React.FC<MediaManagementProps> = ({ 
  onTempUpload, 
  onCancelUpload 
}) => {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([
    {
      id: 'homepage-bg-video',
      title: '홈페이지 배경 비디오',
      description: '홈페이지 메인 배경 영상',
      src: '',
      type: 'video'
    },
    {
      id: 'about-us-video',
      title: 'About Us 영상',
      description: '회사 소개 영상',
      src: '',
      type: 'video'
    },
    {
      id: 'our-values-image',
      title: 'Our Values 이미지',
      description: '우리의 가치 이미지',
      src: '',
      type: 'image'
    },
    {
      id: 'organization-image',
      title: 'Organization 이미지',
      description: '조직도 이미지',
      src: '',
      type: 'image'
    }
  ]);
  
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // 편집 다이얼로그 상태
  const [editingItem, setEditingItem] = useState<MediaItem | null>(null);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<any>(null);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    loadMediaData();
  }, []);

  const loadMediaData = async () => {
    setLoading(true);
    try {
      const { aboutApi } = await import('../../services/api');
      
      // About API에서 모든 데이터 로드 (mainVisual, aboutUs, ourValues, organization 포함)
      const aboutData = await aboutApi.get();
      
      console.log('서버에서 로드된 데이터:', aboutData);
      
      // 기존 미디어 아이템을 업데이트
      setMediaItems(prev => prev.map(item => {
        if (item.id === 'homepage-bg-video' && aboutData?.mainVisual?.backgroundVideo) {
          return { ...item, src: aboutData.mainVisual.backgroundVideo };
        }
        if (item.id === 'about-us-video' && aboutData?.aboutUs?.videoUrl) {
          return { ...item, src: aboutData.aboutUs.videoUrl };
        }
        if (item.id === 'our-values-image' && aboutData?.ourValues?.imageUrl) {
          return { ...item, src: aboutData.ourValues.imageUrl };
        }
        if (item.id === 'organization-image' && aboutData?.organization?.imageUrl) {
          return { ...item, src: aboutData.organization.imageUrl };
        }
        return item;
      }));
    } catch (error) {
      console.error('미디어 데이터 로드 실패:', error);
      setErrorMessage('미디어 데이터를 불러오는데 실패했습니다.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { aboutApi } = await import('../../services/api');
      
      // 현재 서버 데이터를 먼저 가져옴
      const currentData = await aboutApi.get();
      
      // 업데이트할 데이터 준비
      const updateData = { ...currentData };
      
      // 각 미디어 아이템을 해당하는 필드에 저장
      for (const item of mediaItems) {
        if (item.id === 'homepage-bg-video') {
          if (!updateData.mainVisual) updateData.mainVisual = {};
          updateData.mainVisual.backgroundVideo = item.src;
        } else if (item.id === 'about-us-video') {
          if (!updateData.aboutUs) updateData.aboutUs = {};
          updateData.aboutUs.videoUrl = item.src;
        } else if (item.id === 'our-values-image') {
          if (!updateData.ourValues) updateData.ourValues = {};
          updateData.ourValues.imageUrl = item.src;
        } else if (item.id === 'organization-image') {
          if (!updateData.organization) updateData.organization = {};
          updateData.organization.imageUrl = item.src;
        }
      }
      
      console.log('저장할 데이터:', updateData);
      
      // 전체 데이터를 업데이트
      await aboutApi.update(updateData);
      
      setShowSuccess(true);
    } catch (error) {
      console.error('저장 실패:', error);
      setErrorMessage('저장에 실패했습니다.');
      setShowError(true);
    } finally {
      setSaving(false);
    }
  };

  const handleEditItem = (item: MediaItem) => {
    setEditingItem(item);
    setSelectedMedia(null);
    setActiveStep(0);
    setShowItemDialog(true);
  };

  const handleMediaSelect = (media: any) => {
    setSelectedMedia(media);
    setActiveStep(1);
  };

  const handleConfirmSelection = () => {
    if (editingItem && selectedMedia) {
      const updatedItem = { ...editingItem, src: selectedMedia.url };
      setEditingItem(updatedItem);
      
      // 미디어 아이템 목록 업데이트
      setMediaItems(prev => 
        prev.map(item => 
          item.id === editingItem.id ? updatedItem : item
        )
      );
      
      setActiveStep(2);
    }
  };

  const handleSaveItem = () => {
    if (editingItem) {
      const existingIndex = mediaItems.findIndex(item => item.id === editingItem.id);
      const updatedItems = existingIndex >= 0
        ? mediaItems.map(item => item.id === editingItem.id ? editingItem : item)
        : [...mediaItems, editingItem];
      setMediaItems(updatedItems);
    }
    setShowItemDialog(false);
    setEditingItem(null);
    setSelectedMedia(null);
    setActiveStep(0);
  };

  const handleResetSelection = () => {
    setSelectedMedia(null);
    setActiveStep(0);
  };

  const steps = [
    {
      label: '미디어 선택',
      description: '업로드된 미디어 중에서 선택하거나 새로 업로드하세요.',
    },
    {
      label: '선택 확인',
      description: '선택한 미디어를 확인하고 적용하세요.',
    },
    {
      label: '완료',
      description: '미디어가 성공적으로 적용되었습니다.',
    },
  ];

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          미디어 데이터를 불러오는 중...
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Work sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              콘텐츠 관리
            </Typography>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            홈페이지와 About 페이지의 영상 및 이미지 콘텐츠를 관리합니다.
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              홈페이지 및 About 페이지 미디어 관리
            </Typography>
            <Typography variant="body2" color="textSecondary">
              각 항목을 클릭하여 편집할 수 있습니다.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {mediaItems.map((item) => (
              <Grid item xs={12} sm={6} key={item.id}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      boxShadow: 3,
                      borderColor: 'primary.main',
                      transform: 'translateY(-2px)',
                    }
                  }}
                  onClick={() => handleEditItem(item)}
                >
                  <Box sx={{ position: 'relative' }}>
                    <MediaPreview
                      src={item.src || '/img/placeholder.png'}
                      alt={item.title}
                      title={item.title}
                      type={item.type}
                      width="100%"
                      height={200}
                      showPreviewButton={false}
                      showTypeChip={false}
                    />
                    
                    {/* 상태 표시 */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        zIndex: 2,
                      }}
                    >
                      {item.src ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="설정됨"
                          color="success"
                          size="small"
                          sx={{ backgroundColor: 'rgba(76, 175, 80, 0.9)', color: 'white' }}
                        />
                      ) : (
                        <Chip
                          icon={<CloudUpload />}
                          label="미설정"
                          color="warning"
                          size="small"
                          sx={{ backgroundColor: 'rgba(255, 152, 0, 0.9)', color: 'white' }}
                        />
                      )}
                    </Box>
                  </Box>
                  
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {item.type === 'video' ? 
                        <VideoFile sx={{ mr: 1, color: 'primary.main' }} /> : 
                        <Image sx={{ mr: 1, color: 'primary.main' }} />
                      }
                      <Typography variant="h6" component="div">
                        {item.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {item.description}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {item.src ? '미디어 파일이 설정됨' : '미디어 파일이 설정되지 않음'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSave}
              disabled={saving}
              size="large"
              sx={{ minWidth: 120 }}
            >
              {saving ? '저장 중...' : '저장'}
            </Button>
          </Box>
        </Paper>

        {/* 미디어 아이템 편집 다이얼로그 */}
        <Dialog 
          open={showItemDialog} 
          onClose={() => setShowItemDialog(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">
                {editingItem?.title} 편집
              </Typography>
              <Chip
                icon={editingItem?.type === 'video' ? <VideoFile /> : <Image />}
                label={editingItem?.type === 'video' ? '비디오' : '이미지'}
                color="primary"
                variant="outlined"
              />
            </Box>
          </DialogTitle>
          <DialogContent>
            {editingItem && (
              <Box sx={{ mt: 2 }}>
                {/* 스텝퍼 */}
                <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 3 }}>
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel>{step.label}</StepLabel>
                      <StepContent>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          {step.description}
                        </Typography>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>

                <Divider sx={{ my: 2 }} />

                {/* 스텝별 콘텐츠 */}
                {activeStep === 0 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      미디어 선택
                    </Typography>
                    <MediaManager
                      onMediaSelect={handleMediaSelect}
                      filterType={editingItem.type}
                      showUpload={true}
                      showPreview={true}
                      showEdit={false}
                      showDelete={false}
                      multiple={false}
                    />
                  </Box>
                )}

                {activeStep === 1 && selectedMedia && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      선택한 미디어 확인
                    </Typography>
                    <Card sx={{ mb: 2 }}>
                      <Box sx={{ position: 'relative' }}>
                        <MediaPreview
                          src={selectedMedia.url}
                          alt={selectedMedia.name}
                          title={selectedMedia.name}
                          type={selectedMedia.type}
                          width="100%"
                          height={300}
                          showPreviewButton={true}
                          showTypeChip={true}
                        />
                      </Box>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {selectedMedia.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          파일 크기: {(selectedMedia.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          업로드 날짜: {selectedMedia.uploadDate}
                        </Typography>
                      </CardContent>
                    </Card>
                    
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={handleResetSelection}
                        startIcon={<Refresh />}
                      >
                        다시 선택
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleConfirmSelection}
                        startIcon={<CheckCircle />}
                      >
                        이 미디어로 설정
                      </Button>
                    </Box>
                  </Box>
                )}

                {activeStep === 2 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      설정 완료
                    </Typography>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      미디어가 성공적으로 설정되었습니다!
                    </Alert>
                    <Card>
                      <Box sx={{ position: 'relative' }}>
                        <MediaPreview
                          src={editingItem.src}
                          alt={editingItem.title}
                          title={editingItem.title}
                          type={editingItem.type}
                          width="100%"
                          height={300}
                          showPreviewButton={true}
                          showTypeChip={true}
                        />
                      </Box>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {editingItem.title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {editingItem.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowItemDialog(false)}>
              취소
            </Button>
            {activeStep === 2 && (
              <Button onClick={handleSaveItem} variant="contained">
                완료
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* 성공/실패 알림 */}
        <Snackbar
          open={showSuccess}
          autoHideDuration={3000}
          onClose={() => setShowSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setShowSuccess(false)} severity="success">
            미디어가 성공적으로 저장되었습니다!
          </Alert>
        </Snackbar>

        <Snackbar
          open={showError}
          autoHideDuration={5000}
          onClose={() => setShowError(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setShowError(false)} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}; 